import { z } from 'my-zod'
import { outfitterWebRolesZEnum } from '../outfitter-user/outfitter-users-model'
import { operatorGroupFeatureFlagOptions } from '../../outfitter-account-management/outfitter-group/operator-group-feature-flags'
import { operatorGroupsModel } from '../../outfitter-account-management/outfitter-group'

export const clientTypeEnum = z.enum([
  'POLARIS_USER',
  'OUTFITTER_USER',
  'CUSTOMER',
  'OUTFITTER',
  'WAREHOUSE',
  'PUBLIC_GUEST',
  'EHS_CUSTOMER'
])

const RolesEnum = z.enum([
  'Account Specialist',
  'Admin',
  'Adventures Fleet Manager',
  'Corporate Fleet Manager',
  'Corporate Fleet Admin',
  'Employee Polaris Owned Unit Manager',
  'Risk Specialist',
  'RShiny',
  'QuickSight Reader',
  'VPN Prod',
  'VPN Prod Read Replica',
  'VPN Non-Prod',
  'Report Viewer',
  'Warehouse Specialist',
  'Finance',
  'Operator',
  'Sales Representative',
  'Sales Manager',
  'Developer',
  'Member',
  'Customer Support',
  'Catalog Admin',
  'Universal Lead',
  'Customer Support Lead',
  'Manager',
  'Service Ops',
  'PSS-Legal-ENG',
  'Stripe Admin',
  'Business Development',
  'Factory Direct Advantage (University of Polaris)'
])

const analyticsSchema = z
  .object({
    outfitterId: z.number().nullish(),
    outfitterGroupId: z.number().nullish(),
    $ip: z.string().nullish(),
    $browser: z.string().nullish(),
    $os: z.string().nullish(),
    site: z.string().nullish()
  })
  .optional()

export const PolarisUserProfile = z.object({
  clientType: z.literal('POLARIS_USER'),
  name: z.string(),
  email: z.string(),
  roles: RolesEnum.array(),
  polarisUserId: z.number(),
  profileId: z.number(),
  analytics: analyticsSchema,
  maxAge: z.onumber()
})
export type PolarisUserProfile = z.infer<typeof PolarisUserProfile>

export const OutfitterUserProfile = z.object({
  clientType: z.literal('OUTFITTER_USER'),
  name: z.string(),
  email: z.string(),
  role: outfitterWebRolesZEnum,
  outfitterGroupId: z.number(),
  defaultOutfitterId: z.number(),
  outfitterIds: z.number().array().nonempty(),
  profileId: z.number(),
  analytics: analyticsSchema,
  maxAge: z.number().nullish(),
  featureFlags: z.enum(operatorGroupFeatureFlagOptions).array(),
  featurePackage: operatorGroupsModel.shape.featurePackage
})
export type OutfitterUserProfile = z.infer<typeof OutfitterUserProfile>

export const CustomerProfile = z.object({
  clientType: z.literal('CUSTOMER'),
  name: z.string().optional(),
  email: z.string().optional(),
  customerId: z.string().uuid().optional(),
  type: z.enum(['Subscriber', 'CreditPurchaser', 'CreditHolder', 'AccountHolder', 'Guest']),
  profileId: z.number().optional(),
  analytics: analyticsSchema,
  maxAge: z.onumber(),
  sessionId: z.string(),
  impersonator: z.onumber()
})
export type CustomerProfile = z.infer<typeof CustomerProfile>

export const DevProfile = z.object({
  clientType: z.literal('DEV'),
  name: z.string(),
  email: z.string(),
  polarisUserId: z.number(),
  profileId: z.number(),
  analytics: analyticsSchema,
  maxAge: z.onumber()
})
export type DevProfile = z.infer<typeof DevProfile>

export const OutfitterProfile = z.object({
  clientType: z.literal('OUTFITTER'),
  name: z.string(),
  email: z.string(),
  outfitterId: z.number(),
  groupOutfitterIds: z.number().array(),
  roles: z.tuple([z.literal('Operator')]),
  profileId: z.number(),
  analytics: analyticsSchema,
  maxAge: z.onumber()
})
export type OutfitterProfile = z.infer<typeof OutfitterProfile>

export const WarehouseProfile = z.object({
  clientType: z.literal('WAREHOUSE'),
  name: z.string(),
  email: z.string(),
  warehouseUserId: z.number(),
  warehouseId: z.number(),
  profileId: z.number(),
  analytics: analyticsSchema,
  maxAge: z.onumber()
})
export type WarehouseProfile = z.infer<typeof WarehouseProfile>

export const EhsCustomerProfile = z.object({
  clientType: z.literal('EHS_CUSTOMER'),
  name: z.string(),
  email: z.string(),
  ehsCustomerId: z.string().uuid(),
  profileId: z.number(),
  analytics: analyticsSchema,
  maxAge: z.onumber()
})
export type EhsCustomerProfile = z.infer<typeof EhsCustomerProfile>

// Used for completely public routes.  (So far just used for waiver web routes)
export const PublicProfile = z.object({
  clientType: z.literal('PUBLIC_GUEST'),
  profileId: z.number(),
  analytics: analyticsSchema,
  maxAge: z.onumber()
})

export type PublicProfile = z.infer<typeof PublicProfile>

export const IntegrationProfile = z.object({
  clientType: z.literal('INTEGRATION'),
  name: z.string(),
  email: z.string(),
  integration: z.enum(['Alteryx', 'Posthook', 'VLP', 'BillingService', 'Rainforest', 'Viator']),
  profileId: z.number(),
  analytics: analyticsSchema,
  maxAge: z.onumber()
})

export type IntegrationProfile = z.infer<typeof IntegrationProfile>

export const ZapierOutfitterIntegrationProfile = z.object({
  clientType: z.literal('ZAPIER'),
  name: z.string(),
  email: z.string(),
  profileId: z.number(),
  analytics: analyticsSchema,
  maxAge: z.onumber(),
  outfitterId: z.number()
})

export type ZapierOutfitterIntegrationProfile = z.infer<typeof ZapierOutfitterIntegrationProfile>

export const SystemProfile = z.object({
  clientType: z.literal('SYSTEM'),
  name: z.string(),
  email: z.string(),
  profileId: z.number(),
  analytics: analyticsSchema,
  maxAge: z.onumber()
})

export type SystemProfile = z.infer<typeof SystemProfile>

export const DefaultSystemProfile: SystemProfile = {
  clientType: 'SYSTEM',
  name: 'The System',
  email: 'devops@marcatopartners.com',
  profileId: -1
}

export const Profile = z.union([
  PolarisUserProfile,
  OutfitterUserProfile,
  CustomerProfile,
  OutfitterProfile,
  WarehouseProfile,
  EhsCustomerProfile,
  PublicProfile,
  IntegrationProfile,
  SystemProfile,
  DevProfile
])

export type Profile = z.infer<typeof Profile>
